/**
 * Barème et calendrier URSSAF — micro-entrepreneur (artisan) avec versement
 * libératoire de l'impôt sur le revenu.
 *
 * Les taux sont calés sur la déclaration officielle : pour un CA de 3 771 €
 * de prestations + 865 € de marchandises, l'URSSAF appelle 1 012 € répartis
 * en cotisations (905 €), CFP (14 €), taxe CCI/CMA (20 €) et versement
 * libératoire (73 €).
 */

export const CATEGORIES = {
  prestations: {
    key: 'prestations',
    label: 'Prestations de services',
    short: 'Prestations',
    urssaf: 'BIC prestations de services commerciales ou artisanales',
    cotisations: 0.212,
    liberatoire: 0.017,
    cfp: 0.003,
    chambre: 0.0048,
  },
  marchandises: {
    key: 'marchandises',
    label: 'Ventes de marchandises',
    short: 'Marchandises',
    urssaf: 'BIC ventes de marchandises et assimilées',
    cotisations: 0.123,
    liberatoire: 0.010,
    cfp: 0.003,
    chambre: 0.0022,
  },
};

/** Taux global appliqué au CA d'une catégorie (23,68 % / 13,82 %). */
export const globalRate = (cat) =>
  cat.cotisations + cat.liberatoire + cat.cfp + cat.chambre;

export const RATE_LABEL = {
  prestations: `${(globalRate(CATEGORIES.prestations) * 100).toFixed(2).replace('.', ',')} %`,
  marchandises: `${(globalRate(CATEGORIES.marchandises) * 100).toFixed(2).replace('.', ',')} %`,
};

/* ---------------------------------------------------------------- helpers */

export const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v.seconds) return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

export const parsePrice = (p) => {
  if (typeof p === 'number') return isNaN(p) ? 0 : p;
  if (typeof p === 'string') return parseFloat(p.replace(',', '.')) || 0;
  return 0;
};

/** Libellé d'une ligne de prestation — les données historiques utilisent
 *  `title`, le formulaire de saisie utilise `product`. */
export const itemLabel = (p) =>
  String((p && (p.title || p.product || p.designation || p.name || p.label)) || '').trim();

export const lineTotal = (p) => (Number(p?.quantity) || 0) * parsePrice(p?.unitPrice);

export const invoiceTotal = (inv) =>
  Array.isArray(inv?.products) ? inv.products.reduce((a, p) => a + lineTotal(p), 0) : 0;

export const isPaid = (inv) =>
  inv?.paid === true || inv?.status === 'paid' || inv?.status === 'paye';

/**
 * Date retenue pour rattacher une facture à un trimestre.
 * L'URSSAF se déclare à l'encaissement : on utilise la date de paiement
 * quand elle est connue, sinon la date de prestation, sinon la date d'émission.
 */
export const basisDate = (inv) =>
  toDate(inv?.paidDate || inv?.paidAt || inv?.datePaiement) ||
  toDate(inv?.prestaDate) ||
  toDate(inv?.created);

/** true si la facture est rattachée à sa date d'encaissement réelle. */
export const hasCashDate = (inv) => !!toDate(inv?.paidDate || inv?.paidAt || inv?.datePaiement);

/* --------------------------------------------------------------- trimestres */

const ORDINAL = ['1er', '2ème', '3ème', '4ème'];

export const makeQuarter = (year, q) => {
  const start = new Date(year, (q - 1) * 3, 1, 0, 0, 0, 0);
  const end = new Date(year, q * 3, 0, 23, 59, 59, 999);
  // La déclaration est ouverte jusqu'au dernier jour du mois suivant la fin
  // du trimestre (T2 clos le 30/06 → déclaration jusqu'au 31/07).
  const due = new Date(year, q * 3 + 1, 0, 23, 59, 59, 999);
  return {
    id: `${year}-T${q}`,
    year,
    q,
    start,
    end,
    due,
    label: `${ORDINAL[q - 1]} trimestre ${year}`,
    short: `T${q} ${year}`,
  };
};

export const quarterOf = (date) =>
  makeQuarter(date.getFullYear(), Math.floor(date.getMonth() / 3) + 1);

export const shiftQuarter = (qr, delta) => {
  const idx = qr.year * 4 + (qr.q - 1) + delta;
  return makeQuarter(Math.floor(idx / 4), (idx % 4) + 1);
};

export const currentQuarter = (now = new Date()) => quarterOf(now);

/**
 * Trimestre dont la déclaration est ouverte aujourd'hui, ou null.
 * Le 29/07 on est dans le T3 mais c'est le T2 qu'il faut déclarer et payer.
 */
export const openDeclaration = (now = new Date()) => {
  const prev = shiftQuarter(quarterOf(now), -1);
  return now <= prev.due ? prev : null;
};

export const daysUntil = (date, now = new Date()) => {
  const a = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a - b) / 86400000);
};

export const fmtDay = (d) =>
  d ? d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

export const fmtDayShort = (d) =>
  d ? d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—';

/* ------------------------------------------------------------------ calculs */

/**
 * Détail des cotisations, arrondi à l'euro poste par poste comme le fait
 * l'URSSAF (le CA déclaré est lui aussi arrondi à l'euro).
 */
export const computeCharges = (revP, revM) => {
  const P = CATEGORIES.prestations;
  const M = CATEGORIES.marchandises;
  const baseP = Math.round(revP);
  const baseM = Math.round(revM);
  const r = Math.round;

  const cotisationsP = r(baseP * P.cotisations);
  const cotisationsM = r(baseM * M.cotisations);
  const liberatoireP = r(baseP * P.liberatoire);
  const liberatoireM = r(baseM * M.liberatoire);
  const cfp = r(baseP * P.cfp + baseM * M.cfp);
  const chambre = r(baseP * P.chambre + baseM * M.chambre);

  const cotisations = cotisationsP + cotisationsM;
  const liberatoire = liberatoireP + liberatoireM;
  const total = cotisations + liberatoire + cfp + chambre;
  const base = baseP + baseM;

  return {
    baseP, baseM, base,
    cotisationsP, cotisationsM, cotisations,
    liberatoireP, liberatoireM, liberatoire,
    cfp, chambre, total,
    net: base - total,
    effectiveRate: base > 0 ? total / base : 0,
  };
};

/** Agrège les factures d'une période et calcule les charges associées. */
export const computePeriod = (invoices, start, end) => {
  let revP = 0;
  let revM = 0;
  let unpaidCount = 0;
  let unpaidAmount = 0;
  let estimatedCount = 0;
  const list = [];

  (invoices || []).forEach((inv) => {
    const d = basisDate(inv);
    if (!d || d < start || d > end) return;
    let p = 0;
    let m = 0;
    (inv.products || []).forEach((pr) => {
      const r = lineTotal(pr);
      if (pr.isMerchandise) m += r;
      else p += r;
    });
    revP += p;
    revM += m;
    const total = p + m;
    list.push({ ...inv, _p: p, _m: m, _total: total, _date: d });
    if (!isPaid(inv)) {
      unpaidCount += 1;
      unpaidAmount += total;
    }
    if (!hasCashDate(inv)) estimatedCount += 1;
  });

  list.sort((a, b) => b._date - a._date);

  return {
    revP,
    revM,
    revenue: revP + revM,
    invoices: list,
    count: list.length,
    unpaidCount,
    unpaidAmount,
    estimatedCount,
    charges: computeCharges(revP, revM),
  };
};

/** Répartition mensuelle du CA sur une période. */
export const monthlyBreakdown = (invoices, start, end) => {
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor <= end) {
    const mStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const mEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59, 999);
    const p = computePeriod(invoices, mStart, mEnd);
    months.push({
      label: mStart.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }),
      total: p.revenue,
      count: p.count,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
};

export const URSSAF_URL = 'https://www.autoentrepreneur.urssaf.fr';
